import { useQuery } from "@tanstack/react-query";
import { getCurrentUser } from "@/api/users";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useUserStore } from "@/stores/user";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

export const ProfileView = () => {
  const { isLoading: basicProfileLoading } = useUserProfile();
  const basicProfile = useUserStore((state) => state.profile);

  // Fetch full user data with role information
  const { data: fullUser, isLoading: userLoading } = useQuery({
    queryKey: ["current-user"],
    queryFn: getCurrentUser
  });

  const isLoading = basicProfileLoading || userLoading;

  if (isLoading && !basicProfile && !fullUser) {
    return (
      <>
        <Breadcrumb />
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <p className="animate-pulse text-slate-500">Loading profile…</p>
        </div>
      </>
    );
  }

  if (!basicProfile && !fullUser) {
    return (
      <>
        <Breadcrumb />
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-sm text-red-700">
          We couldn't load your user profile. Please refresh the page.
        </div>
      </>
    );
  }

  return (
    <>
    <Breadcrumb />
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold text-slate-900">Your Profile</h2>
        <p className="text-sm text-slate-600">Account information and role details from Content Tracker.</p>
      </header>

      <section className="flex flex-col gap-6 rounded-2xl border border-slate-200 bg-white p-8 md:flex-row shadow-sm">
        <div
          className="flex h-20 w-20 items-center justify-center rounded-2xl text-2xl font-semibold text-white shadow-sm"
          style={{ backgroundColor: basicProfile?.avatar_color || '#0EA5E9' }}
        >
          {basicProfile?.initials || fullUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
        </div>
        <div className="flex flex-1 flex-col justify-center">
          <div className="grid gap-2 text-sm text-slate-700">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Display name</p>
              <p className="text-base text-slate-900">{basicProfile?.display_name || fullUser?.name}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Email</p>
              <p className="text-base text-slate-900">{basicProfile?.email || fullUser?.email}</p>
            </div>
            {fullUser?.role && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Role</p>
                <p className="text-base text-slate-900">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                    fullUser.role === 'admin'
                      ? 'bg-red-100 text-red-700 ring-red-200'
                      : fullUser.role === 'moderator'
                      ? 'bg-blue-100 text-blue-700 ring-blue-200'
                      : 'bg-slate-100 text-slate-700 ring-slate-200'
                  }`}>
                    {fullUser.role.charAt(0).toUpperCase() + fullUser.role.slice(1)}
                  </span>
                </p>
              </div>
            )}
            {fullUser?.department && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Department</p>
                <p className="text-base text-slate-900">{fullUser.department}</p>
              </div>
            )}
            {(basicProfile?.user_id || fullUser?.id) && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">User ID</p>
                <p className="text-base text-slate-700">{basicProfile?.user_id || fullUser?.id}</p>
              </div>
            )}
            {fullUser?.last_login && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Last login</p>
                <p className="text-base text-slate-700">
                  {new Date(fullUser.last_login).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};
